/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AuthResult,
  LoginCodeStatusTypes,
  PaymentPeriod,
  PlatformType,
  UserConsentsState,
  UserSubscriptionStatus,
} from "../../../enums";
import {
  IAuthRequestModel,
  IAuthResponseModel,
  IAuthSignUpRequestModel,
  IAuthSignUpResponseModel,
  IAuthViaTokenRequestModel,
  ICreatePaymentOrderRequestModel,
  IErrorModel,
  IOrderStatusModel,
  IPaymentDetailsModel,
  IPaymentInvoiceModel,
  IPaymentOfferModel,
  IPaymentOrderModel,
  ITokenModel,
  IUserConsentModel,
  IUserInfoModel,
  IUserModel,
  IUserSubscriptionModel,
  IValidCouponModel,
  ICreatePaymentDetailsModel,
  ILocaleModel,
  IProductModel,
} from "../../../models";
import { authStorage, sessionManager, HttpFactory } from "../../../services";
import {
  AuthService,
  HttpRequestFulfilledInterceptor,
  HttpRequestRejectedInterceptor,
  HttpResponseFulfilledInterceptor,
  HttpResponseRejectedInterceptor,
  LocalizationService,
  PauseSubscriptionService,
  PaymentsService,
  UserService,
} from "./services";
import { lastValueFrom, mergeMap, Observable, shareReplay } from "rxjs";
import { ModelsMapperHelper } from "./helpers";
import { pollUntil } from "@nf/helpers";
import { AppConfig } from "@nf/constants";
import {
  ICancelSubscriptionRequestModel,
  IChangePasswordModel,
  IChangePasswordResponseModel,
  ICreateOrderRequestModel,
  ICreatePaymentDetailsRequestModel,
  ICustomerModel,
  IGetLoginTokenFromCodeModel,
  IGetOffersRequestModel,
  ILoginCodeModel,
  ILoginCodeResponseModel,
  ILoginViaTokenRequestModel,
  IOfferSwitchModel,
  IPauseSubscriptionRequestModel,
  ISubscriptionSwitchRequestModel,
  IUnpauseSubscriptionRequestModel,
  IVerifyLoginCodeResponseModel,
  OrderStatus,
  OrderType,
} from "./models";
import { LocaleHelper, StorageHelper } from "../../../helpers";
import { map, switchMap } from "rxjs/operators";
import { toError } from "../../../store/operators";
import { AuthStore, dispatch } from "../../../store";

export class SkymillDataProvider {
  authService = new AuthService();
  userService = new UserService();
  httpFactory = HttpFactory.getInstance("skymill");
  paymentsService = new PaymentsService();
  pauseSubscriptionService = new PauseSubscriptionService();
  localizationService = new LocalizationService();

  constructor() {
    this.setupSessionManager();
  }

  initHttpFactory(country: string) {
    this.httpFactory.baseUrl = AppConfig.ApiAuthUrl;
    this.httpFactory.addRequestInterceptor(
      "HttpRequestInterceptor",
      (config) => HttpRequestFulfilledInterceptor(config, country),
      HttpRequestRejectedInterceptor
    );
    this.httpFactory.addResponseInterceptor(
      "HttpResponseInterceptor",
      HttpResponseFulfilledInterceptor,
      HttpResponseRejectedInterceptor
    );
  }

  setupSessionManager() {
    sessionManager.on("refresh-success", async (session) => {
      await StorageHelper.setSession(session);
      dispatch(AuthStore.Actions.refreshTokenSuccess(session));
    });

    sessionManager.on("refresh-failure", async () => {
      await StorageHelper.clearUserData();
      await StorageHelper.deleteSession();
      dispatch(AuthStore.Actions.refreshTokenFailure());
    });

    sessionManager?.setRefreshFn(async () => {
      const session = StorageHelper.getSession();
      const refreshToken = session?.RefreshToken;

      if (!refreshToken) {
        throw new Error("No refresh token");
      }

      const response = await this.refreshToken(refreshToken);

      if (!response?.Token) {
        throw new Error("Invalid refresh response");
      }

      const newSession = {
        ...session,
        Token: response.Token,
      };

      StorageHelper.setSession(session);

      return newSession;
    });
  }

  signIn(data: IAuthRequestModel): Observable<IAuthResponseModel> {
    const deviceType =
      data.Device?.PlatformCode === PlatformType.Tizen ||
      data.Device?.PlatformCode === PlatformType.TitanOS ||
      data.Device?.PlatformCode === PlatformType.WebOS
        ? PlatformType.SmartTV
        : data.Device?.PlatformCode;

    const payload = {
      email: data.Username || "",
      password: data.Password || "",
      device_id: data.Device?.Id || "",
      device_name: data.Device?.Name,
      device_type: deviceType,
    };

    return this.authService.login(payload).pipe(
      shareReplay(2),
      map((response) => ModelsMapperHelper.toAuthResponseModel(response)),
      mergeMap(async (results) => {
        StorageHelper.setSession(results.AuthorizationToken);
        return results;
      }),
      switchMap((authResponse) =>
        this.authService.customer().pipe(
          map((customer) => ModelsMapperHelper.toUserInfoModel(customer)),
          map((user) => ({ ...authResponse, User: user }))
        )
      )
    );
  }

  async signUp(
    data: IAuthSignUpRequestModel
  ): Promise<IAuthSignUpResponseModel> {
    try {
      const response = await lastValueFrom(
        this.authService.signup({
          email: data.Email,
          utm_source: data.UtmSource,
          utm_medium: data.UtmMedium,
          utm_campaign: data.UtmCampaign,
        })
      );

      return Promise.resolve(
        ModelsMapperHelper.toAuthSignUpResponseModel(response)
      );
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async validatePassword(password: string, email?: string): Promise<boolean> {
    try {
      const device = await StorageHelper.getDevice();
      const payload = {
        email: email || "",
        password: password,
        device_id: device.Id,
      };
      const response = await lastValueFrom(this.authService.login(payload));
      const result = ModelsMapperHelper.toAuthResponseModel(response);

      StorageHelper.setSession(result.AuthorizationToken);

      return true;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  loginViaToken(
    data: IAuthViaTokenRequestModel
  ): Observable<IAuthResponseModel> {
    const deviceType =
      data.Device?.PlatformCode === PlatformType.Tizen ||
      data.Device?.PlatformCode === PlatformType.TitanOS ||
      data.Device?.PlatformCode === PlatformType.WebOS
        ? PlatformType.SmartTV
        : data.Device?.PlatformCode;

    const payload: ILoginViaTokenRequestModel = {
      loginToken: data.LoginToken,
      device_id: data.Device?.Id || "",
      device_name: data.Device?.Name,
      device_type: deviceType || "WEB",
    };

    return this.authService
      .loginViaToken(payload)
      .pipe(
        map((response) => ModelsMapperHelper.toAuthResponseModel(response))
      );
  }

  signInViaToken(
    data: IAuthViaTokenRequestModel
  ): Observable<IAuthResponseModel> {
    return this.loginViaToken(data).pipe(
      mergeMap(async (result) => {
        StorageHelper.setSession(result.AuthorizationToken);
        return result;
      }),
      switchMap((authResponse) =>
        this.authService.customer().pipe(
          map((customer) => ModelsMapperHelper.toUserInfoModel(customer)),
          map((user) => ({ ...authResponse, User: user }))
        )
      )
    );
  }

  async getCustomer(): Promise<IUserInfoModel> {
    try {
      return await lastValueFrom(
        this.authService
          .customer()
          .pipe(map((response) => ModelsMapperHelper.toUserInfoModel(response)))
      );
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async signOut(refreshToken: string): Promise<void> {
    try {
      const payload = {
        refresh_token: refreshToken,
      };
      await lastValueFrom(this.authService.logout(payload));
      authStorage.deleteSession();
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  public async refreshToken(refreshToken: string): Promise<ITokenModel> {
    try {
      const response = await lastValueFrom(
        this.authService.refreshToken({
          refresh_token: refreshToken,
        })
      );
      const token: ITokenModel = {
        AuthResult: AuthResult.OK,
        Token: response.idToken,
      };
      return Promise.resolve(token);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getProfile(): Promise<IUserModel> {
    try {
      const response = await lastValueFrom(this.userService.profile());
      const user = ModelsMapperHelper.toUserModel(response);
      return Promise.resolve(user);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async updateProfile(data: Partial<IUserModel>): Promise<IUserModel> {
    try {
      const payload: Partial<ICustomerModel> = {
        first_name: data.FirstName,
        last_name: data.LastName,
        phone_prefix: data.PhoneNumberPrefix,
        phone_number: data.PhoneNumberNumber,
      };

      const response = await Promise.all([
        lastValueFrom(this.userService.updateProfile(payload)),
        data.Email && lastValueFrom(this.userService.updateEmail(data.Email)),
      ]);

      const user = ModelsMapperHelper.toUserModel({
        ...response[1],
        ...response[0],
      });

      return Promise.resolve(user);
    } catch (error: any) {
      if (error?.message && error.message.includes("Request throttled"))
        error.message = "request_throttled";

      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getUserConsents(): Promise<IUserConsentModel[]> {
    try {
      const response = await Promise.all([
        lastValueFrom(this.userService.availableConsents()),
        lastValueFrom(this.userService.consents()),
      ]);

      return ModelsMapperHelper.toConsentModel(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  updateUserConsents(
    data: IUserConsentModel[]
  ): Observable<IUserConsentModel[]> {
    const payload = {
      consents: data.map((consent) => ({
        name: consent.ConsentName,
        state: consent.Accepted
          ? UserConsentsState.Accepted
          : UserConsentsState.Declined,
        version: consent.ConsentVersion,
      })),
    };

    return this.userService.updateUserConsents(payload).pipe(map(() => data));
  }

  async changePassword(
    data: IChangePasswordModel
  ): Promise<IChangePasswordResponseModel> {
    try {
      return await lastValueFrom(this.authService.changePassword(data));
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  getUserSubscriptions(): Observable<IUserSubscriptionModel[] | IErrorModel> {
    return this.userService.subscriptions().pipe(
      map(({ subscriptions }) =>
        subscriptions.map((subscription) =>
          ModelsMapperHelper.toSubscriptionModel(subscription)
        )
      ),
      toError(ModelsMapperHelper.toErrorModel)
    );
  }

  async cancelUserSubscription(data: ICancelSubscriptionRequestModel) {
    try {
      const response = await lastValueFrom(
        this.userService.cancelSubscription(data)
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await lastValueFrom(
            this.paymentsService.getOrderStatus(response.order_id)
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          if (orderStatus.order.type === OrderType.CANCEL_SUB_IN_SWITCH) {
            return (
              orderStatus.order.status === OrderStatus.COMPLETED &&
              orderStatus.subscription?.status ===
                UserSubscriptionStatus.TERMINATED
            );
          }

          return (
            (orderStatus.order.status === OrderStatus.COMPLETED &&
              orderStatus.subscription?.status ===
                UserSubscriptionStatus.CANCELLED) ||
            orderStatus.subscription?.status ===
              UserSubscriptionStatus.TERMINATED // `in_dunning` subscription returns draft + terminated
          );
        },
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async uncancelUserSubscription(id: number) {
    try {
      const response = await lastValueFrom(
        this.userService.uncancelSubscription(id)
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await lastValueFrom(
            this.paymentsService.getOrderStatus(response.order_id)
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.status === OrderStatus.COMPLETED &&
            orderStatus.subscription?.status === UserSubscriptionStatus.ACTIVE
          );
        },
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async pauseUserSubscription(data: IPauseSubscriptionRequestModel) {
    try {
      const response = await lastValueFrom(
        this.pauseSubscriptionService.pauseSubscription(data)
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await lastValueFrom(
            this.paymentsService.getOrderStatus(response.order_id)
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.type === OrderType.PAUSE_SUB &&
            orderStatus.order.status === OrderStatus.COMPLETED
          );
        },
      });

      return Promise.resolve({ Message: "SUBSCRIPTION_PAUSED" });
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async unpauseUserSubscription(data: IUnpauseSubscriptionRequestModel) {
    try {
      const response = await lastValueFrom(
        this.pauseSubscriptionService.unpauseSubscription(data)
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await lastValueFrom(
            this.paymentsService.getOrderStatus(response.order_id)
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.type === OrderType.UNPAUSE_SUB &&
            orderStatus.order.status === OrderStatus.COMPLETED
          );
        },
      });

      return Promise.resolve({ Message: "SUBSCRIPTION_UNPAUSED" });
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  getOffers(data?: IGetOffersRequestModel): Observable<IPaymentOfferModel[]> {
    return this.paymentsService.getOffers(data).pipe(
      map(({ offers }) => {
        const monthlyOffer = offers.find(
          (offer) => offer.period === PaymentPeriod.Month
        );
        const monthlyPrice = monthlyOffer?.price ? +monthlyOffer.price : 0;
        const annualBaselinePrice = monthlyPrice * 12;

        return offers.map((offer) => {
          const paymentOffer = ModelsMapperHelper.toPaymentOfferModel(offer);

          if (
            paymentOffer.Period === PaymentPeriod.Year &&
            !paymentOffer.SavingsPrice
          ) {
            paymentOffer.SavingsPrice = annualBaselinePrice;
          }

          return paymentOffer;
        });
      }),
      map((offers) => [
        ...offers.filter((offer) => offer.Period === PaymentPeriod.Month),
        ...offers.filter((offer) => offer.Period === PaymentPeriod.Year),
      ])
    );
  }

  async getProducts(): Promise<IProductModel[]> {
    try {
      const response = await lastValueFrom(this.paymentsService.getProducts());
      const products = response.products.map((product) =>
        ModelsMapperHelper.toProductModel(product)
      );

      return Promise.resolve([
        ...products.filter((product) => product.Period === PaymentPeriod.Month),
        ...products.filter((product) => product.Period === PaymentPeriod.Year),
      ]);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async createPaymentDetails(
    data: ICreatePaymentDetailsModel
  ): Promise<IPaymentOrderModel> {
    const payload: ICreatePaymentDetailsRequestModel = {
      accept_url: data.AcceptUrl,
      cancel_url: data.CancelUrl,
    };

    try {
      const response = await lastValueFrom(
        this.paymentsService.createPaymentDetails(payload)
      );

      return Promise.resolve(ModelsMapperHelper.toPaymentOrderModel(response));
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async createPaymentOrder(
    data: ICreatePaymentOrderRequestModel
  ): Promise<IPaymentOrderModel> {
    const payload: ICreateOrderRequestModel = {
      offer_id: data.OfferId,
      coupon_code: data.CouponCode,
      offer_handle: data.OfferHandle,
      accept_url: data.AcceptUrl,
      cancel_url: data.CancelUrl,
    };

    try {
      const response = await lastValueFrom(
        this.paymentsService.createOrder(payload)
      );
      return Promise.resolve(ModelsMapperHelper.toPaymentOrderModel(response));
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  getPaymentInvoices(): Observable<IPaymentInvoiceModel[]> {
    return this.paymentsService
      .paymentInvoices()
      .pipe(
        map((response) =>
          response.invoices.map((invoice) =>
            ModelsMapperHelper.toInvoiceModel(invoice)
          )
        )
      );
  }

  async getInvoicePDF(invoiceId: string): Promise<string> {
    try {
      return await lastValueFrom(this.paymentsService.getInvoicePDF(invoiceId));
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getPaymentDetails(): Promise<IPaymentDetailsModel | undefined> {
    try {
      const response = await lastValueFrom(
        this.paymentsService.getPaymentDetails()
      );
      const payment = response?.[0];

      if (payment) {
        return Promise.resolve(
          ModelsMapperHelper.toPaymentDetailsModel(payment)
        );
      }

      return;
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async validateCoupon(coupon: string): Promise<IValidCouponModel> {
    try {
      const response = await lastValueFrom(
        this.paymentsService.validateCoupon(coupon)
      );
      return Promise.resolve(ModelsMapperHelper.toValidCouponModel(response));
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getOrderStatus(orderId: string): Promise<IOrderStatusModel> {
    try {
      const response = await lastValueFrom(
        this.paymentsService.getOrderStatus(orderId)
      );
      return Promise.resolve(ModelsMapperHelper.toOrderStatusModel(response));
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getLoginCode(): Promise<ILoginCodeResponseModel> {
    try {
      const response = await lastValueFrom(this.authService.getLoginCode());
      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async verifyLoginCode(
    data: ILoginCodeModel
  ): Promise<IVerifyLoginCodeResponseModel> {
    try {
      const payload = {
        code: data.code,
        refresh_token: data.refresh_token,
      };
      const response = await lastValueFrom(
        this.authService.verifyCode(payload)
      );

      if (response.status === LoginCodeStatusTypes.expired) {
        return Promise.reject(ModelsMapperHelper.toErrorModel(response.status));
      }

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getLoginTokenFromCode(
    data: ILoginCodeModel
  ): Promise<IGetLoginTokenFromCodeModel> {
    try {
      const payload = {
        code: data.code,
      };
      const response = await lastValueFrom(
        this.authService.getLoginTokenFromCode(payload)
      );

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getOfferSwitches(): Promise<IOfferSwitchModel[]> {
    try {
      const response = await lastValueFrom(this.userService.offerSwitches());
      const offerSwitches = response.map((offerSwitch) =>
        ModelsMapperHelper.toOfferSwitchModel(offerSwitch)
      );

      return Promise.resolve(offerSwitches);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async switchUserSubscription(
    data: ISubscriptionSwitchRequestModel
  ): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.userService.switchSubscription(data)
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await lastValueFrom(
            this.paymentsService.getOrderStatus(response.order_id)
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.status === OrderStatus.COMPLETED &&
            orderStatus.subscription?.status ===
              UserSubscriptionStatus.PENDING_SWITCH
          );
        },
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async cancelUserSubscriptionSwitch(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.userService.cancelSubscriptionSwitch()
      );

      await pollUntil({
        fetch: async () => {
          const orderStatus = await lastValueFrom(
            this.paymentsService.getOrderStatus(response.order_id)
          );
          return orderStatus;
        },
        isResolved: (orderStatus) => {
          if (!orderStatus) return false;

          return (
            orderStatus.order.status === OrderStatus.COMPLETED &&
            orderStatus.subscription?.status ===
              UserSubscriptionStatus.TERMINATED
          );
        },
      });

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getUserCountry(): Promise<string | undefined> {
    try {
      const response = await lastValueFrom(this.localizationService.locales());
      return Promise.resolve(response.cloudfront_viewer_country?.code);
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }

  async getLocales(): Promise<ILocaleModel[]> {
    try {
      const response = await lastValueFrom(this.localizationService.locales());
      return Promise.resolve(
        response.locales.map(ModelsMapperHelper.toLocaleModel)
      );
    } catch (error) {
      return Promise.reject(ModelsMapperHelper.toErrorModel(error));
    }
  }
}
